import Button from '@/components/Button';
import { ButtonVariant } from '@/components/ButtonVariant';
import { CmsArticleItem } from '@/components/CmsInterfaces';
import { DropdownSelect } from '@/components/DropdownSelect';
import { IconVariant } from '@/components/Icon';
import OffLine from '@/components/OffLine';
import OfflinePlaceholder from '@/components/OfflinePlaceholder';
import OnLine from '@/components/OnLine';
import { TeaserTile } from '@/components/TeaserTile';
import { newsConfig } from '@/config/news';
import React from 'react';

interface NewsOverviewState {
  filter: string;
  articles: CmsArticleItem[];
  currentAmount: number;
}

export interface NewsOverviewProps {
  title: string;
  children?: React.ReactNode;
}

export class NewsOverview extends React.Component<
  NewsOverviewProps,
  NewsOverviewState
> {
  constructor(props) {
    super(props);
    this.state = {
      articles: [],
      currentAmount: newsConfig.chunkSize,
      filter: newsConfig.allFilter,
    };
  }

  async componentDidMount() {
    const response = await fetch(`/api/data/all-articles`);
    if (response.ok) {
      this.setState({ articles: await response.json() });
    }

    const filter = this.initialFilter;
    if (this.state.filter !== filter) {
      this.handleOnFilter(filter);
    }
  }

  private get initialFilter(): string {
    if (URLSearchParams && window) {
      const param = new URLSearchParams(window.location.search).get(
        newsConfig.filterParam
      );
      const filter = param && newsConfig.filters[param] ? param : undefined;
      if (filter) {
        this.saveFilterToSession(filter);
        return filter;
      }
      return NewsOverview.savedFilter;
    }
    return NewsOverview.savedFilter;
  }

  private static get savedFilter(): string {
    try {
      if (typeof sessionStorage !== 'undefined') {
        const saved = sessionStorage.getItem(newsConfig.filterKey);
        return saved !== null ? saved : newsConfig.allFilter;
      }
    } catch {
      //
    }
    return newsConfig.allFilter;
  }

  private filterFunction = (a: CmsArticleItem): boolean =>
    a.category === this.state.filter ||
    this.state.filter === newsConfig.allFilter;

  private handleOnFilter = (filter: string): void => {
    if (newsConfig.filters[filter]) {
      this.setState({ filter, currentAmount: newsConfig.chunkSize }, () => {
        this.saveFilterToSession(filter);
        if (this.filteredArticles.length === 0) {
          // this.fetchMore();
        }
      });
    }
  };

  private handleOnMore = (): void => {
    const oldLength = this.filteredArticles.length;
    const oldAmount = this.state.currentAmount;
    const newAmount =
      oldLength < oldAmount ? oldAmount : oldAmount + newsConfig.chunkSize;
    this.setState({ currentAmount: newAmount });
  };

  private saveFilterToSession = (filter: string): void => {
    try {
      if (typeof sessionStorage !== 'undefined') {
        sessionStorage.setItem(newsConfig.filterKey, filter);
      }
    } catch {
      //
    }
  };

  private get filteredArticles(): CmsArticleItem[] {
    return this.state.articles.filter(this.filterFunction);
  }

  private get canShowMore(): boolean {
    return this.filteredArticles.length > this.state.currentAmount;
  }

  private static getItemKey(item: CmsArticleItem): string {
    return item.path === '' ? item.datetime.toString() : item.path;
  }

  private drawTile(item: CmsArticleItem): JSX.Element {
    return (
      <TeaserTile
        title={item.title}
        text={item.teaser}
        date={item.date}
        link={item.path}
        image={item.image}
        category={item.category}
      />
    );
  }

  private renderItems = (): JSX.Element[] => {
    return this.filteredArticles
      .slice(0, this.state.currentAmount)
      .map((item) => (
        <div className="NewsOverview__item" key={NewsOverview.getItemKey(item)}>
          {this.drawTile(item)}
        </div>
      ));
  };

  render(): JSX.Element {
    const canShowMore = this.canShowMore;
    return (
      <section className="NewsOverview">
        <header className="NewsOverview__header">
          <h1 className="NewsOverview__title">{this.props.title}</h1>
          <OnLine>
            <DropdownSelect
              selected={this.state.filter}
              onChange={this.handleOnFilter}
              options={newsConfig.filters}
              icon={IconVariant.Filter}
            />
          </OnLine>
        </header>
        <OnLine>
          <div className="NewsOverview__grid">{this.renderItems()}</div>
          {canShowMore && (
            <footer className="NewsOverview__footer">
              <Button
                disabled={!this.canShowMore}
                variant={ButtonVariant.Loading}
                onClick={this.handleOnMore}
              >
                Mehr anzeigen
              </Button>
            </footer>
          )}
        </OnLine>
        <OffLine>
          <OfflinePlaceholder box={true} />
        </OffLine>
      </section>
    );
  }
}

export default NewsOverview;
